import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Divider,
  FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper,
  Select, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, ArrowBack as BackIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuItemService from '../../../../services/menuItemService';
import menuSectionService from '../../../../services/menuSectionService';
import menuFormatService from '../../../../services/menuFormatService';
import menuItemFormatService from '../../../../services/menuItemFormatService';
import inventoryItemService from '../../../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../../../services/inventoryItemService';
import FormatExecutionBlock from '../../../../components/venue/FormatExecutionBlock';
import type {
  MenuFormat, MenuItemFormat as MenuItemFormatDoc, MenuItemKind, MenuSection, MenuVisibility,
} from '../../../../types/menu';

/**
 * Generic non-wine item form (Phase 7.C.2b). One form for COCKTAIL and DISH —
 * they share the same spine (displaySnapshot.name, serving-size rows, the
 * per-serving Stock & recipe block) and differ only in copy + the payload kind.
 * WINE keeps its own form (MenuItemFormWine) because of wineDetails.
 */
type NonWineKind = Extract<MenuItemKind, 'COCKTAIL' | 'DISH' | 'BEER' | 'SPIRIT' | 'RETAIL' | 'OTHER'>;

interface KindLabels {
  itemLabel: string;
  itemNameLabel: string;
  placeholder: string;
  menuSectionTitle: string;
}

const KIND_LABELS: Record<NonWineKind, KindLabels> = {
  COCKTAIL: {
    itemLabel: 'Cocktail',
    itemNameLabel: 'Cocktail name',
    placeholder: 'e.g. Margarita',
    menuSectionTitle: 'Cocktail details',
  },
  DISH: {
    itemLabel: 'Dish',
    itemNameLabel: 'Dish name',
    placeholder: 'e.g. Grilled chicken',
    menuSectionTitle: 'Dish details',
  },
  BEER: {
    itemLabel: 'Beer',
    itemNameLabel: 'Beer name',
    placeholder: 'e.g. Pilsner Urquell',
    menuSectionTitle: 'Beer details',
  },
  SPIRIT: {
    itemLabel: 'Spirit',
    itemNameLabel: 'Spirit name',
    placeholder: 'e.g. Talisker 10',
    menuSectionTitle: 'Spirit details',
  },
  RETAIL: {
    itemLabel: 'Item',
    itemNameLabel: 'Item name',
    placeholder: 'e.g. Gift box',
    menuSectionTitle: 'Retail item details',
  },
  OTHER: {
    itemLabel: 'Item',
    itemNameLabel: 'Item name',
    placeholder: 'e.g. Item name',
    menuSectionTitle: 'Item details',
  },
};

interface FormData {
  name: string;
  subtitle: string;
  basePrice: string;
  currency: string;
  sectionId: string;
  visibility: MenuVisibility;
  notes: string;
  externalUrl: string;
  needsReview: boolean;
  position: string;
}

interface FormatRow {
  /** Server id when persisted, undefined when this row is new. */
  _id?: string;
  formatId: string;
  /** Free-text price to support typing in progress. Validated on save. */
  price: string;
  visibility: MenuVisibility;
  inventoryItem: InventoryItemWithBalance | null;
  consumptionRecipeId: string | null;
}

const emptyFormData = (defaultCurrency: string): FormData => ({
  name: '',
  subtitle: '',
  basePrice: '',
  currency: defaultCurrency,
  sectionId: '',
  visibility: 'LIVE',
  notes: '',
  externalUrl: '',
  needsReview: false,
  position: '',
});

/**
 * Generic non-wine item create / edit form (COCKTAIL or DISH).
 *
 * - `kind` (prop) is sent verbatim in the submit payload. No UI exposes it.
 * - wineDetails is never set.
 * - Per-format pricing is edited inline. Save diffs against the originally
 *   loaded rows: new rows are created, changed rows patched, removed rows deleted.
 *
 * Phase 7.C.2b generalised the former MenuItemFormCocktail (extracted in 4.B.a)
 * into this kind-parameterised form so DISH reuses the cocktail spine exactly.
 * No behavior change for cocktails.
 */
interface MenuItemFormNonWineProps {
  kind: NonWineKind;
}

const MenuItemFormNonWine: React.FC<MenuItemFormNonWineProps> = ({ kind }) => {
  const navigate = useNavigate();
  const { menuId, itemId } = useParams<{ menuId: string; itemId?: string }>();
  const theme = useTheme();
  const { venueId, defaultCurrency } = useVenue();
  const isEdit = Boolean(itemId);
  const backPath = `/admin/venues/${venueId}/menus/${menuId}`;
  const labels = KIND_LABELS[kind];

  const [formData, setFormData] = useState<FormData>(emptyFormData(defaultCurrency));
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // Sections + formats lookups
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [formats, setFormats] = useState<MenuFormat[]>([]);

  // Item-level inventory link (loaded + round-tripped; control hidden in 7.C.1).
  const [linkedItem, setLinkedItem] = useState<InventoryItemWithBalance | null>(null);

  // Per-item format pricing
  const [formatRows, setFormatRows] = useState<FormatRow[]>([]);
  const [originalFormatRows, setOriginalFormatRows] = useState<FormatRow[]>([]);

  // ── Phase 7.A.2c — execution-item link/create support ─────────────────────
  // Suggested execution-item name for a serving-size row: "<item> — <format>".
  const suggestedExecutionName = useCallback(
    (formatId: string): string => {
      const f = formats.find(x => x._id === formatId);
      const label = f?.name ?? '';
      return [formData.name.trim(), label].filter(Boolean).join(' — ');
    },
    [formats, formData.name],
  );

  // After a link/create from the execution block, sync BOTH formatRows and
  // originalFormatRows for that row so the form's diff-save never overwrites
  // inventoryItemId with a stale value.
  const handleLinkedItemChange = useCallback(
    (formatRowId: string, item: InventoryItemWithBalance | null) => {
      setFormatRows(rows => rows.map(r => (r._id === formatRowId ? { ...r, inventoryItem: item } : r)));
      setOriginalFormatRows(rows => rows.map(r => (r._id === formatRowId ? { ...r, inventoryItem: item } : r)));
    },
    [],
  );

  // ── Load sections / formats ───────────────────────────────────────────────
  useEffect(() => {
    if (!menuId) return;
    menuSectionService.getSections(venueId, menuId)
      .then(setSections)
      .catch(() => { /* non-blocking */ });
    menuFormatService.getFormats(venueId, menuId)
      .then(setFormats)
      .catch(() => { /* non-blocking */ });
  }, [venueId, menuId]);

  // ── Load existing item in edit mode ───────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !menuId || !itemId) return;
    setLoading(true);
    menuItemService.getItem(venueId, menuId, itemId)
      .then(item => {
        setFormData({
          name: item.displaySnapshot?.name ?? '',
          subtitle: item.displaySnapshot?.subtitle ?? '',
          basePrice: item.basePrice != null ? String(item.basePrice) : '',
          currency: item.currency ?? defaultCurrency,
          sectionId: item.sectionId ?? '',
          visibility: item.visibility,
          notes: item.notes ?? '',
          externalUrl: item.externalUrl ?? '',
          needsReview: Boolean(item.needsReview),
          position: item.position != null ? String(item.position) : '',
        });
        if (item.inventoryItemId) {
          inventoryItemService.getItem(venueId, item.inventoryItemId)
            .then(inv => setLinkedItem(inv as unknown as InventoryItemWithBalance))
            .catch(() => { /* non-blocking */ });
        }
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [isEdit, menuId, itemId, venueId, defaultCurrency]);

  // ── Load existing per-item format pricings in edit mode ───────────────────
  useEffect(() => {
    if (!isEdit || !itemId) return;
    menuItemFormatService.getItemFormats(venueId, itemId)
      .then(async (rows: MenuItemFormatDoc[]) => {
        // Map server rows to FormatRow. Fetch each linked inventory item
        // separately so the autocomplete displays the right label.
        const mapped: FormatRow[] = await Promise.all(rows.map(async r => {
          let invItem: InventoryItemWithBalance | null = null;
          if (r.inventoryItemId) {
            try {
              invItem = (await inventoryItemService.getItem(venueId, r.inventoryItemId)) as unknown as InventoryItemWithBalance;
            } catch { /* non-blocking */ }
          }
          return {
            _id: r._id,
            formatId: typeof r.formatId === 'string' ? r.formatId : (r.formatId as any)._id,
            price: String(r.price),
            visibility: r.visibility,
            inventoryItem: invItem,
            consumptionRecipeId: r.consumptionRecipeId ?? null,
          };
        }));
        setFormatRows(mapped);
        // Snapshot the original rows for diff-save. Use cloned values so later
        // mutations to formatRows don't accidentally mutate the original baseline.
        setOriginalFormatRows(mapped.map(r => ({ ...r })));
      })
      .catch(() => { /* non-blocking */ });
  }, [isEdit, itemId, venueId]);

  // ── Generic field change ──────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required.';
    if (formData.basePrice && (isNaN(parseFloat(formData.basePrice)) || parseFloat(formData.basePrice) < 0)) {
      newErrors.basePrice = 'Must be a non-negative number.';
    }
    if (formData.position && isNaN(parseInt(formData.position, 10))) {
      newErrors.position = 'Must be a whole number.';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    // Format pricing rows
    const seenFormats = new Set<string>();
    for (const row of formatRows) {
      if (!row.formatId) { setApiError('Every format pricing row needs a format selected.'); return false; }
      if (seenFormats.has(row.formatId)) {
        setApiError('A format can be priced only once per item.');
        return false;
      }
      seenFormats.add(row.formatId);
      const priceNum = parseFloat(row.price);
      if (!row.price || isNaN(priceNum) || priceNum < 0) {
        setApiError('Every format pricing row needs a non-negative price.');
        return false;
      }
    }
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!menuId) return;
    setSaving(true);
    setApiError('');
    try {
      const basePrice = formData.basePrice ? parseFloat(formData.basePrice) : undefined;
      const position = formData.position ? parseInt(formData.position, 10) : 0;

      const displaySnapshot = {
        name: formData.name.trim(),
        ...(formData.subtitle.trim() && { subtitle: formData.subtitle.trim() }),
      };

      let savedItemId = itemId;
      if (isEdit && itemId) {
        await menuItemService.updateItem(venueId, menuId, itemId, {
          displaySnapshot,
          basePrice,
          currency: formData.currency.trim() || undefined,
          sectionId: formData.sectionId === '' ? null : formData.sectionId,
          visibility: formData.visibility,
          notes: formData.notes.trim() || undefined,
          externalUrl: formData.externalUrl.trim() || undefined,
          needsReview: formData.needsReview,
          position,
          inventoryItemId: linkedItem ? linkedItem._id : null,
        });
      } else {
        const created = await menuItemService.createItem(venueId, menuId, {
          kind,
          displaySnapshot,
          basePrice,
          currency: formData.currency.trim() || undefined,
          sectionId: formData.sectionId === '' ? null : formData.sectionId,
          visibility: formData.visibility,
          source: 'MANUAL',
          notes: formData.notes.trim() || undefined,
          externalUrl: formData.externalUrl.trim() || undefined,
          needsReview: formData.needsReview,
          position,
          inventoryItemId: linkedItem ? linkedItem._id : null,
        } as Parameters<typeof menuItemService.createItem>[2]);
        savedItemId = created._id;
      }

      // ── Diff and persist per-item format pricings ─────────────────────────
      if (savedItemId) {
        const originalById = new Map(originalFormatRows.filter(r => r._id).map(r => [r._id!, r]));
        const currentIds = new Set(formatRows.filter(r => r._id).map(r => r._id!));

        // Deletes: original ids no longer present in the current rows.
        for (const orig of originalFormatRows) {
          if (orig._id && !currentIds.has(orig._id)) {
            await menuItemFormatService.deleteItemFormat(venueId, savedItemId, orig._id);
          }
        }
        // Creates + updates.
        for (const row of formatRows) {
          const priceNum = parseFloat(row.price);
          const rowInvId = row.inventoryItem ? row.inventoryItem._id : null;
          if (!row._id) {
            await menuItemFormatService.createItemFormat(venueId, savedItemId, {
              formatId: row.formatId,
              price: priceNum,
              visibility: row.visibility,
              inventoryItemId: rowInvId,
              consumptionRecipeId: row.consumptionRecipeId,
            });
          } else {
            const orig = originalById.get(row._id);
            const origPrice = orig ? parseFloat(orig.price) : NaN;
            const origInvId = orig?.inventoryItem ? orig.inventoryItem._id : null;
            const origRecipeId = orig?.consumptionRecipeId ?? null;
            const origVisibility = orig?.visibility;
            const changed =
              origPrice !== priceNum ||
              origInvId !== rowInvId ||
              origRecipeId !== row.consumptionRecipeId ||
              origVisibility !== row.visibility;
            if (changed) {
              await menuItemFormatService.updateItemFormat(venueId, savedItemId, row._id, {
                price: priceNum,
                visibility: row.visibility,
                inventoryItemId: rowInvId,
                consumptionRecipeId: row.consumptionRecipeId,
              });
            }
          }
        }
      }
      navigate(backPath);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const backendMessage = err?.response?.data?.message;
      if (code === 'KIND_NOT_SUPPORTED_YET') {
        setApiError(`Only ${labels.itemLabel.toLowerCase()} items can be created or edited in this phase.`);
      } else if (code === 'DISPLAY_SNAPSHOT_REQUIRED') {
        setErrors(prev => ({ ...prev, name: 'Name is required.' }));
      } else if (code === 'SECTION_NOT_VALID') {
        setApiError('The selected section is no longer active on this menu.');
      } else {
        setApiError(backendMessage ?? 'Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Format row helpers ────────────────────────────────────────────────────
  const addFormatRow = () => {
    setFormatRows(rows => [
      ...rows,
      { formatId: '', price: '', visibility: 'LIVE', inventoryItem: null, consumptionRecipeId: null },
    ]);
  };

  const updateFormatRow = (idx: number, patch: Partial<FormatRow>) => {
    setFormatRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const removeFormatRow = (idx: number) => {
    setFormatRows(rows => rows.filter((_, i) => i !== idx));
  };

  // ── Early returns ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadFailed) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Menu
        </Button>
        <Alert severity="error">
          Could not load this {labels.itemLabel.toLowerCase()}. It may have been removed. Go back and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Menu
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? `Edit ${labels.itemLabel}` : `Add ${labels.itemLabel}`}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* Phase 4.L — visual restructure into four product-facing sections:
              Identity / Pricing & serving / Inventory & operations / Notes.
              The form fields themselves and the backend payload shape are
              unchanged — only grouping and copy were edited.

              Developer-only architecture context (intentionally not surfaced
              in the UI):
                · Recipes are persisted on MenuItemFormat (per serving size),
                  not directly on the MenuItem.
                · `inventoryItemId` is a default reference today; nothing
                  deducts stock from it yet — POS linkage is a future phase.
                · `consumptionRecipeId` on each MenuItemFormat row is stored
                  but inactive for sales / depletion. */}

          {/* SECTION 1 — Identity */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{labels.menuSectionTitle}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              What the {labels.itemLabel.toLowerCase()} is and where it appears on the menu.
            </Typography>
            <Stack spacing={2}>
              <TextField
                label={labels.itemNameLabel}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={Boolean(errors.name)}
                helperText={errors.name ?? `Required. Shown as the ${labels.itemLabel.toLowerCase()} name on the menu.`}
                fullWidth
                required
                placeholder={labels.placeholder}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              <TextField
                label="Subtitle"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g. Tequila, Lime, Triple Sec"
                helperText="Optional. Shown as a one-line description under the name."
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 240, flex: 1 }}>
                  <InputLabel>Section</InputLabel>
                  <Select
                    value={formData.sectionId}
                    label="Section"
                    onChange={e => setFormData(prev => ({ ...prev, sectionId: e.target.value }))}
                  >
                    <MenuItem value=""><em>Unsectioned</em></MenuItem>
                    {sections.map(s => (
                      <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 240, flex: 1 }}>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={formData.visibility}
                    label="Visibility"
                    onChange={e => setFormData(prev => ({ ...prev, visibility: e.target.value as MenuVisibility }))}
                  >
                    <MenuItem value="LIVE">Live — visible on menu</MenuItem>
                    <MenuItem value="HIDDEN">Hidden — not shown</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  error={Boolean(errors.position)}
                  helperText={errors.position ?? 'Lower = earlier'}
                  type="number"
                  sx={{ width: 120 }}
                  slotProps={{ htmlInput: { min: 0, step: 1 } }}
                />
              </Box>
            </Stack>
          </Paper>

          {/* SECTION 2 — Pricing & serving */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Pricing & serving</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Base price plus one entry per serving size, with an optional recipe per size.
            </Typography>
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Base Price"
                  name="basePrice"
                  value={formData.basePrice}
                  onChange={handleInputChange}
                  error={Boolean(errors.basePrice)}
                  helperText={errors.basePrice ?? 'Optional. Default price when no per-size price is set.'}
                  type="number"
                  sx={{ flex: 2, minWidth: 140 }}
                  slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                />
                <TextField
                  label="Currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  sx={{ width: 130 }}
                  placeholder={defaultCurrency || 'GBP'}
                  helperText={defaultCurrency ? `Default: ${defaultCurrency}` : undefined}
                  slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
                />
              </Box>

              {/* Serving sizes block.
                  Architecture note for developers — this still maps 1:1 to
                  MenuItemFormat rows. Each row carries its own selling
                  price, visibility flag, optional inventory override, and
                  optional consumptionRecipeId. Recipes live on the row
                  rather than the item itself because different serving
                  sizes can use different recipes. */}
              <Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.75,
                    mb: 1.5,
                    borderRadius: 1,
                    borderColor: theme => alpha(theme.palette.info.main, 0.4),
                    backgroundColor: theme => alpha(theme.palette.info.main, 0.04),
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Serving sizes &amp; stock
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.5 }}>
                    Each serving size has its own stock &amp; recipe setup. Add a serving size below and save it to set up stock &amp; recipe.
                  </Typography>
                </Paper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>Serving sizes</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Optional. Each serving size has its own selling price and its own stock &amp; recipe setup. Manage the available serving sizes in the Serving sizes tab.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={formats.length === 0 || formatRows.length >= formats.length}
                    onClick={addFormatRow}
                    sx={{ textTransform: 'none' }}
                  >
                    Add serving size
                  </Button>
                </Box>
                {formats.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                      Create serving sizes for this menu first.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Serving sizes define options like Bottle, 150ml glass, Plate or Bowl. Add them in this menu&apos;s Serving sizes tab, then come back to set prices and stock &amp; recipe.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`${backPath}?tab=formats`)}
                      sx={{ textTransform: 'none' }}
                    >
                      Go to Serving sizes
                    </Button>
                  </Alert>
                ) : formatRows.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No serving sizes configured yet. Click "Add serving size" to add one.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {formatRows.map((row, idx) => {
                      const usedElsewhere = new Set(
                        formatRows.filter((_, i) => i !== idx).map(r => r.formatId).filter(Boolean),
                      );
                      return (
                        <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          {/* Line 1: serving size + selling price + visibility + delete */}
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                            <FormControl size="small" sx={{ flex: 2, minWidth: 180 }}>
                              <InputLabel>Serving size</InputLabel>
                              <Select
                                value={row.formatId}
                                label="Serving size"
                                onChange={e => updateFormatRow(idx, { formatId: e.target.value })}
                              >
                                {formats.map(f => (
                                  <MenuItem
                                    key={f._id}
                                    value={f._id}
                                    disabled={usedElsewhere.has(f._id) && row.formatId !== f._id}
                                  >
                                    {f.name}
                                    {f.formatKind === 'VOLUME' && f.volumeMl != null && ` · ${f.volumeMl} ml`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              label="Selling price"
                              value={row.price}
                              onChange={e => updateFormatRow(idx, { price: e.target.value })}
                              type="number"
                              sx={{ width: 130 }}
                              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                            />
                            <FormControl size="small" sx={{ width: 130 }}>
                              <InputLabel>Visibility</InputLabel>
                              <Select
                                value={row.visibility}
                                label="Visibility"
                                onChange={e => updateFormatRow(idx, { visibility: e.target.value as MenuVisibility })}
                              >
                                <MenuItem value="LIVE">LIVE</MenuItem>
                                <MenuItem value="HIDDEN">HIDDEN</MenuItem>
                              </Select>
                            </FormControl>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeFormatRow(idx)}
                              aria-label="Remove serving size"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          {/* Line 2: per-serving stock & recipe (Phase 7.A.2a/2b/2c) */}
                          {row._id && menuId && itemId ? (
                            <FormatExecutionBlock
                              venueId={venueId}
                              menuId={menuId}
                              itemId={itemId}
                              formatId={row._id}
                              itemKind={kind}
                              suggestedName={suggestedExecutionName(row.formatId)}
                              onLinkedItemChange={handleLinkedItemChange}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Save this item first. Then you can set up Stock &amp; recipe for this serving option.
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Paper>

          {/* SECTION 3 — Inventory & operations */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Links &amp; flags</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Optional. Stock &amp; recipe is set per serving size above.
            </Typography>
            {/* Phase 7.C.1 — the item-level inventory link control is hidden from the
                normal operator UI. Per-serving "Stock & recipe" is now the canonical
                execution link. Any existing linkedItem (MenuItem.inventoryItemId) is
                still loaded and round-tripped in the save payload below. */}
            <Stack spacing={2}>
              <TextField
                label="External link"
                name="externalUrl"
                value={formData.externalUrl}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://..."
                helperText="Optional. Shown as 'View details' on the customer view."
                slotProps={{ htmlInput: { maxLength: 2000 } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.needsReview}
                    onChange={e => setFormData(prev => ({ ...prev, needsReview: e.target.checked }))}
                  />
                }
                label="Needs review"
              />
            </Stack>
          </Paper>

          {/* SECTION 4 — Notes */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Notes</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Optional internal notes. Not shown on the customer-facing menu.
            </Typography>
            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={2}
              placeholder="Tasting notes or list copy (optional)"
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
          </Paper>

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Add to Menu'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MenuItemFormNonWine;
